import type { IObservableArray } from 'mobx';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import type { Flight, Goal, Segments as SegmentsCollection } from '@feathr/blackbox';
import { Card, Fieldset, Form, Input, SaveButton, Tooltip } from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import { useStore } from '@feathr/extender/state';
import { flattenErrors } from '@feathr/hooks';

import { AddGoals, getGoalSegments, validateStepGoals } from '../CampaignEditPage/StepGoals';
import FlightPathSettings from '../FlightPage/MakeSharedFlightModal/FlightPathSettings';

import * as styles from './FlightEditPage.css';

function validate(
  flight: Flight,
  goals: IObservableArray<Goal>,
  Segments: SegmentsCollection,
): string[] {
  const flightErrors = toJS(
    flight.validate(['name', 'shared', 'shared_name', 'shared_description', 'shared_image'], false)
      .errors,
  );
  const goalSegments = getGoalSegments(goals, Segments);
  const goalErrors = flattenErrors(validateStepGoals({ goals, goalSegments }));
  return [...flightErrors, ...goalErrors];
}

function FlightEditPage() {
  const { Flights, Goals, Segments } = useStore();
  const { flightId } = useParams<{ flightId: string; eventId: string }>();
  const { t } = useTranslation();

  const flight = Flights.get(flightId);
  const goals = Goals.list({
    filters: {
      _parent: flight.id,
      is_archived__ne: true,
    },
  });

  async function handleSave() {
    for (const goal of goals.models) {
      if (goal.get('is_archived')) {
        if (!goal.isEphemeral) {
          await goal.patchDirty();
        }
        continue;
      }
      const segmentId = goal.get('segment');
      if (segmentId) {
        const segment = Segments.get(segmentId);
        if (segment.isEphemeral) {
          await Segments.add(segment);
        } else if (segment.isDirty) {
          await segment.patchDirty();
        }
      }
      if (goal.isEphemeral) {
        await Goals.add(goal);
      } else if (goal.isDirty) {
        await goal.patchDirty();
      }
    }
    Flights.refreshApiCache();
  }

  const errors = validate(flight, goals.models, Segments);
  const isValid = errors.length === 0;

  const saveButton = (
    <SaveButton
      disabled={!isValid}
      errorMessage={t("Couldn't save. Try again.")}
      key={'finish'}
      method={'patch'}
      model={flight}
      onSave={handleSave}
      successMessage={t('Changes have been saved.')}
    />
  );

  const actions = [
    isValid ? (
      <Fragment key={'finish'}>{saveButton}</Fragment>
    ) : (
      <Tooltip
        key={'finish'}
        title={errors.map((error, i) => (
          <Fragment key={i}>
            {error}
            {i < errors.length - 1 && <br />}
          </Fragment>
        ))}
      >
        {saveButton}
      </Tooltip>
    ),
  ];

  return (
    <Page pageTitle={t('Edit {{name}}', { name: flight.name })} title={t('Edit')}>
      <Form actions={actions} className={styles.form} label={t('Edit flight')}>
        <Fieldset>
          <Input attribute={'name'} label={t('Name')} model={flight} type={'text'} />
        </Fieldset>
        <Card contentClassName={styles.cardContent} title={t('Goals')}>
          <AddGoals goalKind={'flight'} goals={goals.models} parent={flight} />
        </Card>
        <FlightPathSettings flight={flight} title={t('Flight path')} />
      </Form>
    </Page>
  );
}

export default observer(FlightEditPage);
